import i18n from '@/i18n'
import {isValid} from 'better-dni'
import config from "@/project";
import store from '@/store'
import {WORDS_BANNED, SCAT_ALLOWED} from "@/constants/wordsBanned";

const tlds = [
  'com', 'net', 'es', 'info', 'org', 'xyz', 'io', 'me', 'top', 'in', 'eu', 'ai', 'online', 'us', 'biz', 'tv', 'co', 'tk', 'uk', 'fans'
].join('|')

const wordsBanned = WORDS_BANNED
const scatAllowed = SCAT_ALLOWED

const EmailRegex = /.+@.+\..+/
const PasswordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.{8,})/
const AlphanumericRegex = /^[a-zA-Z0-9_]*$/
const IntegerRegex = /^[0-9]*$/
const PhoneRegex = /(\+34|0034|34)?[ -.,_]*([0-9][ -.,_]*){9,11}/
const LinkRegex = new RegExp(`(http(s?):\\/\\/)?(www.)?[a-zA-Z0-9]+\\.(${tlds})`, 'g')

const getCurrentUser = () => store.state.profile.currentUser

const isScat = () => config.project === 'scatbook' || getCurrentUser().scatProfile

const findBannedWords = (text) => {
  const splitted = text.toLowerCase().split(' ').map(word => word.replace(/[+!?,._-]/g, ''))
  let wordsBannedProject = wordsBanned

  if (isScat()) {
    wordsBannedProject = wordsBanned.filter(word => !scatAllowed.includes(word))
  }

  return splitted.filter(w => wordsBannedProject.includes(w)).join(', ')
}

const findPhoneNumber = (text) => {
  const words = text.split(' ')
  for (const word of words) {
    if (!word.includes('trials')) {
      if (PhoneRegex.test(word)) {
        return word
      }
    }
  }
}

const rules = {
  install(Vue, options) {
    Vue.mixin({
      methods: {
        $vOnlyAlphanumeric: (v) => !v || AlphanumericRegex.test(v) || i18n.t('errors.only_alphanumerics'),
        $vInteger: (v) => !v || IntegerRegex.test(v) || i18n.t('errors.only_integers'),
        $vPhone: (v) => {
          if (!v) return true
          const phone = findPhoneNumber(v)
          return !phone || i18n.t('errors.not_phone')
        },
        $vMin: (min) => (v) => v >= min || i18n.t('errors.min_value', {value: min}),
        $vMax: (max) => (v) => v <= max || i18n.t('errors.max_value', {value: max}),
        $vMaxCurrency: (max) => (v) => v <= max || i18n.t('errors.max_value', {value: max ? parseFloat(max.toString().replace(',', '.')) / 100 : null}),
        $vMinLength: (min, fieldName) => (v) => {
          const length = !!v && v.trim().length
          if (!length || (length >= min)) {
            return true
          }
          const field = fieldName || i18n.t('errors._default_length_field')
          return i18n.t('errors.no_min_length', {min, field})
        },
        $vMaxLength: (max) => (v) => {
          const length = !!v && v.trim().length
          if (!length || (length <= max)) {
            return true
          }
          return i18n.t('errors.no_max_length', {max})
        },

        $vMinMaxLength: (min, max, field) => (v) => {
          const length = !!v && v.trim().length
          if (!length || (length >= min && length <= max)) {
            return true
          }
          const key = field ? 'no_min_max_field_length' : 'no_min_max_length'
          return i18n.t(`errors.${key}`, {min, max, field})
        },
        $vBannedWords: (v) => {
          if (!v) return true
          const word = findBannedWords(v)
          return !word || i18n.t('errors.banned_word', {word})
        },
        $vRequired: (v) => {
          if ((typeof v) === 'string') return !!v.trim() || i18n.t('errors.required')
          return !!v || i18n.t('errors.required')
        },
        $vBoolRequired: (v) => (v) || i18n.t('errors.required'),
        $vNoHtml: (field) => {
          var doc = new DOMParser().parseFromString(field, 'text/html')
          return !Array.from(doc.body.childNodes).some(node => node.nodeType === 1) || i18n.t('errors.no_html_tags')
        },
        $vNoLink: (field) => {
          const splitted = field.toLowerCase().split(' ')
          const links = splitted.filter(w => w.match(LinkRegex))
          return links.filter((w) => {
            if (!w.includes('loverfans') && !w.includes('scatbook') && !w.includes('darkfans') && !w.includes('loverachelle2') && !w.includes('jordienp') && !w.includes('kaitlynkatsarosofficial')) {
              return w
            }
          }).join(', ')
        },
        $vValidNIF: (v) => v != null ? isValid(v.replace(/[^0-9a-zA-Z]/g, '')) || i18n.t('errors.invalidNif') : false,
        $vEmail: (v) => EmailRegex.test(v) || i18n.t('errors.bad_email'),
        $vNoWhiteSpace: (v) => !v.trim().includes(' ') || i18n.t('errors.no_white_spaces'),
        $vPassword: (v) => !v || PasswordRegex.test(v) || i18n.t('errors.bad_password'),
        $vConfirmation: function (field) {
          return (value) => {
            const ref = this.$refs[field]
            return !ref || ref.value === value || this.$t('errors.dont_match')
          }
        },
        $vIsOver18: function (field) {
          const locale = i18n?.locale || navigator.language || 'es'
          const isSpanish = locale.startsWith('es')
          let day, month, year
          if (isSpanish) {
            // Formato DD/MM/YYYY
            [day, month, year] = field.split('/').map(Number)
          } else {
            // Formato MM/DD/YYYY
            [month, day, year] = field.split('/').map(Number)
          }

          const currentDate = new Date();
          const currentYear = currentDate.getFullYear();
          const currentMonth = currentDate.getMonth() + 1;
          const currentDay = currentDate.getDate();

          const age = currentYear - year - (currentMonth < month || (currentMonth === month && currentDay < day) ? 1 : 0);

          return age >= 18 || i18n.t('errors.over_18');
        },
        $vAfterToday: function (field) {
          const parts = field.split('/')
          const today = new Date()
          const inputDate = new Date(parts[2], parts[0], parts[1])
          return inputDate > today || i18n.t('errors.over_18')
        }
      }
    })
  }
}

export function getBannedWords() {
  return wordsBanned
}

export function existBannedWords(text) {
  if (!text) return false
  return findBannedWords(text)
}

export function noHtml(field) {
  if (!field) return false
  var doc = new DOMParser().parseFromString(field, 'text/html')
  return Array.from(doc.body.childNodes).some(node => node.nodeType === 1)
}

export function noLink(text) {
  if (!text) return false
  const splitted = text.toLowerCase().split(' ')
  const links = splitted.filter(w => w.match(LinkRegex))
  return links.filter((w) => {
    if (!w.includes('loverfans') && !w.includes('scatbook') && !w.includes('darkfans') && !w.includes('loverachelle2') && !w.includes('jordienp') && !w.includes('kaitlynkatsarosofficial')) {
      return w
    }
  }).join(', ')
}

export function noPhone(field) {
  if (!field) return false
  return findPhoneNumber(field)
}

export default rules
